/**
 * Logger utility for Noesis Mental Health System
 * Provides structured logging with console output and optional server logging
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogContext = {
  module?: string;
  userId?: number;
  sessionId?: string;
  patientId?: number;
  [key: string]: unknown;
};

class Logger {
  private readonly isDevelopment: boolean;
  private readonly logEndpoint: string;
  private logQueue: Array<{
    level: LogLevel;
    message: string;
    context: LogContext;
    timestamp: string;
  }> = [];
  private flushInterval: number | null = null;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.logEndpoint = process.env.NEXT_PUBLIC_API_URL + '/api/logs';
  }

  private formatMessage(level: LogLevel, message: string, context: LogContext): string {
    const contextStr =
      Object.keys(context).length > 0
        ? ` [${Object.entries(context)
            .map(([k, v]) => `${k}=${v}`)
            .join(' ')}]`
        : '';
    return `[${level.toUpperCase()}]${contextStr} ${message}`;
  }

  private async flushLogs() {
    if (this.logQueue.length === 0) return;

    const logs = [...this.logQueue];
    this.logQueue = [];

    try {
      const response = await fetch(this.logEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionStorage.getItem('auth_token')
            ? { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` }
            : {}),
        },
        body: JSON.stringify({ logs }),
      });

      if (response.ok) {
        console.log('[Logger] Flushed', logs.length, 'logs to server');
      } else {
        // 日志上报失败属于遥测降级，不应作为应用错误呈现
        //（console.error 会触发 Next.js dev 模式的红色错误浮层；
        // 例如应急只读会话曾被 403 拒绝上报，现已加入中间件白名单，此处兜底防同类噪音）
        console.warn('[Logger] Failed to flush logs:', response.status);
      }
    } catch (error) {
      console.warn('[Logger] Failed to send logs to server:', error);
    }
  }

  startAutoFlush(intervalMs: number = 10000) {
    if (this.flushInterval) clearInterval(this.flushInterval);
    this.flushInterval = window.setInterval(() => this.flushLogs(), intervalMs);
  }

  stopAutoFlush() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }

  log(level: LogLevel, message: string, context: LogContext = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = { level, message, context, timestamp };

    // Add to queue
    this.logQueue.push(logEntry);

    // Console output
    const formattedMessage = this.formatMessage(level, message, context);

    const consoleMethod =
      {
        debug: console.log,
        info: console.info,
        warn: console.warn,
        error: console.error,
      }[level] || console.log;

    consoleMethod(formattedMessage);

    // Flush on error immediately
    if (level === 'error') {
      this.flushLogs();
    }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, context?: LogContext) {
    this.log('error', message, context);
  }

  // Convenience methods for common contexts
  api(
    method: string,
    endpoint: string,
    status: number | string,
    context?: Record<string, unknown>
  ) {
    const level: LogLevel =
      typeof status === 'number'
        ? status >= 500
          ? 'error'
          : status >= 400
            ? 'warn'
            : 'debug'
        : 'info';
    this.log(level, `API ${method} ${endpoint} - ${status}`, context);
  }

  ui(component: string, action: string, context?: Record<string, unknown>) {
    this.log('debug', `UI ${component} - ${action}`, context);
  }

  auth(action: string, context?: Record<string, unknown>) {
    this.log('info', `Auth ${action}`, context);
  }

  export(resource: string, format: string, context?: Record<string, unknown>) {
    this.log('info', `Export ${resource} as ${format}`, {
      ...context,
      exported_resource: resource,
      export_format: format,
    });
  }
}

// Singleton instance
export const logger = new Logger();

// Auto-start in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  logger.startAutoFlush();
}

export default logger;
